import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/auth.api';
import { emailLocale } from '../../i18n';
import Spinner from '../../components/ui/Spinner';
import { Mail, ArrowLeft, ArrowRight, Check, Icon } from '../../components/ui/Icon';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  // ADR-0009: quien llega desde el error "esta cuenta entra con Google" ya
  // tecleó su correo en el login; volver a pedírselo es fricción gratuita.
  const [params]              = useSearchParams();
  const [email, setEmail]     = useState(params.get('email') ?? '');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.forgotPassword({ email: email.trim(), locale: emailLocale() });
      setSent(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('auth.forgotPassword.failed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f0' }}>
      <div className="w-full max-w-md px-8">
        {sent ? (
          <>
            <div className="w-14 h-14 mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(11,20,32,0.06)' }}>
              <Icon as={Check} size={26} style={{ color: '#0b1420' }} />
            </div>
            <h1 className="font-serif text-[42px] font-light leading-none tracking-tight mb-4" style={{ color: '#0b1420', letterSpacing: -1.6 }}>
              {t('auth.forgotPassword.sentTitle')}
            </h1>
            <p className="font-serif font-medium text-base leading-relaxed mb-8" style={{ color: '#3a4558' }}>
              {t('auth.forgotPassword.sentBody', { email: email.trim() })}
            </p>
            <p className="text-[13px] leading-relaxed mb-8" style={{ color: '#6b7689' }}>
              {t('auth.forgotPassword.sentHint')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2.5 text-sm font-medium no-underline"
              style={{ color: '#0b1420' }}
            >
              <Icon as={ArrowLeft} size={15} />
              {t('auth.forgotPassword.backToLogin')}
            </Link>
          </>
        ) : (
          <>
            <p className="font-mono text-[10px] tracking-[2px] uppercase mb-3" style={{ color: '#8a5a2b' }}>
              {t('auth.forgotPassword.eyebrow')}
            </p>
            <h1 className="font-serif text-[42px] font-light leading-none tracking-tight mb-4" style={{ color: '#0b1420', letterSpacing: -1.6 }}>
              {t('auth.forgotPassword.title')}
            </h1>
            <p className="font-serif font-medium text-base leading-relaxed mb-8" style={{ color: '#3a4558' }}>
              {t('auth.forgotPassword.subtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
                  {t('auth.forgotPassword.emailLabel')}
                </label>
                <div className="flex items-center gap-2.5" style={{ border: '1px solid #d6dae2', borderRadius: 10, height: 54, background: '#fff', padding: '0 14px' }}>
                  <Icon as={Mail} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
                  <input
                    id="email" type="email"
                    className="flex-1 outline-none bg-transparent text-sm"
                    style={{ fontFamily: 'Inter, sans-serif', color: '#0b1420', height: '100%', border: 'none' }}
                    placeholder="you@example.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    required autoFocus autoComplete="email"
                  />
                </div>
              </div>

              {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">⚠ {error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 font-sans font-medium text-lg transition-all"
                style={{ marginTop: 10, height: 56, background: '#0b1420', color: '#faf7f0', borderRadius: 10, border: 'none', cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? <Spinner size="sm" /> : (
                  <>
                    {t('auth.forgotPassword.submit')}
                    <span className="w-7 h-7 rounded-full flex items-center justify-center not-italic" style={{ background: '#d9b382', color: '#0b1420' }}>
                      <Icon as={ArrowRight} size={13} />
                    </span>
                  </>
                )}
              </button>
            </form>

            <Link
              to="/login"
              className="inline-flex items-center gap-2.5 text-sm font-medium no-underline mt-7"
              style={{ color: '#6b7689' }}
            >
              <Icon as={ArrowLeft} size={15} />
              {t('auth.forgotPassword.backToLogin')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
